// O menu do app, um só para celular (tela Mais) e computador (menu lateral).
// Organizado pelo trabalho: vender, cuidar das clientes, acompanhar o dinheiro e, por último, configurar.
import { useWaitlist } from '../screens/Engage.tsx';
import { can, useDebts, useMachines, useMe } from '../lib/data.ts';
import { money } from '../lib/format.ts';
import type { IconName } from '../ui/Icon.tsx';
import type { View } from './nav.tsx';

export type Badge = 'owed' | 'espera' | 'setup';
export interface MenuItem { view: View; icon: IconName; label: string; perm?: string; badge?: Badge }
export interface MenuGroup { title: string; items: MenuItem[] }

// dia a dia: no celular são as abas de baixo; no computador, o topo do menu lateral
export const MAIN: MenuItem[] = [
  { view: 'hoje', icon: 'home', label: 'Hoje' },
  { view: 'agenda', icon: 'cal', label: 'Agenda' },
  { view: 'clientes', icon: 'users', label: 'Clientes' },
  { view: 'caixa', icon: 'cash', label: 'Caixa', perm: 'valoresSalao' },
];

export const GROUPS: MenuGroup[] = [
  { title: 'Vender', items: [
    { view: 'tabela', icon: 'kit', label: 'Serviços e preços' },
    { view: 'vendas', icon: 'kit', label: 'Vendas no balcão', perm: 'cobrar' },
    { view: 'pacotes', icon: 'spa', label: 'Pacotes' },
    { view: 'vales', icon: 'gift', label: 'Vale-presente', perm: 'cobrar' },
    { view: 'pendencias', icon: 'card', label: 'Pagar depois', badge: 'owed' },
  ] },
  { title: 'Clientes', items: [
    { view: 'lembretes', icon: 'message', label: 'Lembretes de amanhã' },
    { view: 'espera', icon: 'clip', label: 'Lista de espera', badge: 'espera' },
    { view: 'fidelidade', icon: 'ticket', label: 'Fidelidade' },
    { view: 'avaliacoes', icon: 'star', label: 'Avaliações' },
    { view: 'mensagens', icon: 'message', label: 'Mensagens', perm: 'config' },
  ] },
  { title: 'Gestão', items: [
    { view: 'relatorios', icon: 'chart', label: 'Relatórios', perm: 'relatorios' },
    { view: 'despesas', icon: 'arrowOut', label: 'Despesas e lucro', perm: 'valoresSalao' },
    { view: 'repasse', icon: 'team', label: 'Parceiras e repasses', perm: 'valoresSalao' },
  ] },
];

export const SETTINGS: MenuItem = { view: 'setup', icon: 'gear', label: 'Configurações', perm: 'config', badge: 'setup' };

export const ALL_ITEMS: MenuItem[] = [...MAIN, ...GROUPS.flatMap((g) => g.items), SETTINGS];

// com o menu enxuto ligado (Regras do salão), o que não é do dia a dia sai da vista; as telas continuam existindo
export const LEAN_HIDDEN: View[] = ['vendas', 'pacotes', 'vales', 'fidelidade', 'avaliacoes', 'espera', 'meuLink'];

export function visible(items: MenuItem[], me: Parameters<typeof can>[0] & { salon?: { settings?: { leanMenu?: boolean } } }) {
  const lean = !!me.salon?.settings?.leanMenu;
  return items.filter((i) => (!i.perm || can(me, i.perm)) && !(lean && LEAN_HIDDEN.includes(i.view)));
}

// números ao lado dos itens: só quando há algo a fazer
export function useBadges(): Record<Badge, { text: string; late?: boolean } | null> {
  const me = useMe().data;
  const debts = useDebts();
  const wait = useWaitlist();
  const machines = useMachines().data;
  const owed = (debts.data ?? []).reduce((t, d) => t + d.debt, 0);
  const waiting = (wait.data ?? []).filter((w) => w.status === 'waiting').length;
  const st = me?.salon.settings;
  // mesmo critério do "Antes de começar" das Configurações
  const faltam = st ? [!st.pixKey?.trim(), machines && !machines.some((m) => m.active), !st.profile?.whatsapp?.trim(), !st.profile?.address?.trim()].filter(Boolean).length : 0;
  return {
    owed: owed > 0 ? { text: money(owed), late: true } : null,
    espera: waiting > 0 ? { text: String(waiting) } : null,
    setup: faltam > 0 ? { text: `falta${faltam > 1 ? 'm' : ''} ${faltam}`, late: true } : null,
  };
}
